import React, { useEffect, useMemo, useState } from "react";

import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import { MaterialReactTable } from "material-react-table";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { useDispatch, useSelector } from "react-redux";

import { hasPermission } from "../Utils/UtilityFunctions";
import { useColumnSettings } from "../Utils/useColumnSettings";

import { Loader } from "../Utils/Loader";
import CommonModal from "../Utils/CommonModal";

import TransferForm from "./TransferForm";
import { getAllTransfer } from "../../slices/Transfer/GetallTransfer/thunk";

function TransferListings() {
  const dispatch = useDispatch();

  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap,
  );

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)]),
  );

  const stocktranferPermission = hasPermission(
    permissionsMap,
    "transactions",
    "transfer",
  );

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteItemData, setDeleteItemData] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { transferList } = useSelector((state) => state.GetAllTransfer || {});

  useEffect(() => {
    dispatch(
      getAllTransfer(
        pagination.pageSize,
        pagination.pageIndex + 1,
        "transfer",
        setLoading,
      ),
    );
  }, [dispatch, pagination.pageSize, pagination.pageIndex]);

  useEffect(() => {
    if (transferList?.transactions) {
      const formattedData = transferList.transactions
        .filter((item) => item?.transactiontype === "transfer")
        .map((item) => ({
          referenceid: item?.referenceid || "-",
          category: item?.category || "-",
          fromlocation: item?.fromlocation || "-",
          fromlocationname: item?.fromlocationname || "-",
          tolocation: item?.tolocation || "-",
          tolocationname: item?.tolocationname || "-",
          quantity: item?.quantity || 0,
          itemname: item?.itemname || "-",
          itemid: item?.itemid || "-",
        }));

      setFilteredData(formattedData);
    } else {
      setFilteredData([]);
    }
  }, [transferList]);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
  };

  const handleDelete = (item) => {
    setDeleteItemData(item);

    toggleDeleteModal();
  };

  const handleSearch = () => {
    if (!searchText.trim()) {
      const resetData =
        transferList?.transactions
          ?.filter((item) => item?.transactiontype === "transfer")
          ?.map((item) => ({
            referenceid: item?.referenceid || "-",
            category: item?.category || "-",
            fromlocation: item?.fromlocation || "-",
            fromlocationname: item?.fromlocationname || "-",
            tolocation: item?.tolocation || "-",
            tolocationname: item?.tolocationname || "-",
            quantity: item?.quantity || 0,
            itemname: item?.itemname || "-",
            itemid: item?.itemid || "-",
          })) || [];

      setFilteredData(resetData);

      return;
    }

    const search = searchText.toLowerCase();

    const filtered = filteredData.filter((item) => {
      return (
        item?.itemname?.toLowerCase().includes(search) ||
        item?.fromlocationname?.toLowerCase()?.includes(search) ||
        item?.tolocationname?.toLowerCase()?.includes(search) ||
        item?.category?.toLowerCase().includes(search)
      );
    });

    setFilteredData(filtered);
  };

  // EXPORT CSV
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  });

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(filteredData);

    download(csvConfig)(csv);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "itemname",
        header: "Product Name",
        size: 200,
      },

      {
        accessorKey: "category",
        header: "Category",
        size: 180,
      },

      {
        accessorKey: "fromlocationname",
        header: "From Location",
        size: 220,
      },

      {
        accessorKey: "tolocationname",
        header: "To Location",
        size: 220,
      },

      {
        accessorKey: "quantity",
        header: "Quantity",
        size: 120,
        Cell: ({ cell }) => (
          <span>{Number(cell.getValue()).toLocaleString()}</span>
        ),
      },
    ],
    [],
  );

  const { toggleSettingsModal, CustomViewModal } = useColumnSettings(
    "transfer",
    columns,
  );

  const itemOptions =
    transferList?.transactions?.map((item) => ({
      label: item?.itemname,
      value: item?.itemid,
    })) || [];

  return (
    <React.Fragment>
      <div id="csms">
        <Container fluid>
          <Row>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4
                style={{
                  fontWeight: "700",
                  color: "#2b2b2b",
                  marginBottom: "0",
                }}
              >
                Transfer List
              </h4>
            </div>

            {/* SEARCH SECTION */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
              <div className="d-flex align-items-center w-100 gap-2">
                <Input
                  type="text"
                  placeholder="Type to search here"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={{
                    maxWidth: "500px",
                    height: "36px",
                    borderRadius: "4px",
                  }}
                />

                <Button
                  className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap mx-1"
                  onClick={handleSearch}
                >
                  <i className="ri-search-line label-icon align-middle fs-16 me-1 fw-bold"></i>

                  <span className="ps-2 ms-2 me-1 fw-bold">Search</span>
                </Button>
              </div>

              <div className="d-flex align-items-center">
                {stocktranferPermission?.has("export") && (
                  <Button
                    className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={handleExportData}
                  >
                    <i className="ri-download-line label-icon secondary-label-icon align-middle fs-16 me-2 fw-bold"></i>

                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">Export</span>
                  </Button>
                )}

                {stocktranferPermission?.has("create") && (
                  <Button
                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={() => {
                      setSelectedItem(null);

                      toggleModal();
                    }}
                  >
                    <i className="ri-add-fill label-icon align-middle fs-16 me-1 fw-bold"></i>

                    <span className="ps-2 ms-2 me-1 fw-bold">Add Transfer</span>
                  </Button>
                )}

                {/* {stocktranferPermission?.has("view") && (
                  <div
                    className="btn-icon cursor-img"
                    onClick={toggleSettingsModal}
                  >
                    <i className="ri-settings-2-line fs-4"></i>
                  </div>
                )} */}
              </div>
            </div>

            {/* TABLE */}
            <Col lg={12}>
              <div
                style={{
                  border: "1px solid #dcdcdc",
                  borderRadius: "6px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  borderRadius: "4px",
                }}
              >
                {loading && <Loader />}
                <MaterialReactTable
                  columns={columns}
                  data={filteredData}
                  enableColumnOrdering={false}
                  enableColumnDragging={false}
                  enableDensityToggle={false}
                  enableColumnActions={false}
                  enableTopToolbar={false}
                  manualPagination={true}
                  rowCount={transferList?.totalcount || 0}
                  onPaginationChange={setPagination}
                  state={{
                    pagination,
                  }}
                  muiTablePaginationProps={{
                    rowsPerPageOptions: [10, 20, 50, 100],
                  }}
                />
              </div>
            </Col>

            {CustomViewModal()}
          </Row>
        </Container>
      </div>

      <CommonModal
        modalHeader="Delete Transfer"
        open={deleteModal}
        toggle={toggleDeleteModal}
        buttonText="Yes, Delete"
        closeButtonText="Cancel"
        messageForPopUp={`Are you sure you want to delete "${deleteItemData?.item}" ?`}
        modalAction={async () => {
          toggleDeleteModal();
        }}
      />

      {/* ADD / EDIT MODAL */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{
            borderBottom: "1px solid #FD7545",
            padding: "16px 20px",
          }}
        >
          <span
            style={{
              color: "#FD7545",
              fontWeight: "600",
              fontSize: "18px",
            }}
          >
            {selectedItem ? "Edit Transfer" : "Add Transfer"}
          </span>
        </ModalHeader>

        <ModalBody
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            padding: "20px 20px 0px 20px"
          }}
        >
          <TransferForm
            closeModal={toggleModal}
            onSuccess={() => {
              const first = pagination.pageIndex * pagination.pageSize;

              const offset = pagination.pageSize;

              dispatch(
                getAllTransfer(
                  pagination.pageSize,
                  pagination.pageIndex + 1,
                  "transfer",
                  setLoading,
                ),
              );
            }}
            selectedItem={selectedItem}
            itemOptions={itemOptions}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

export default TransferListings;
